import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  TIMED_OUT_MESSAGE,
  getOrgTwins,
  getOrganisations,
  getTokenBounded,
  getTwinById,
} from '@repo/front-end-services';
import { matchTwinOrganisation } from './twinOrganisation';

/**
 * The other twins of the organisation a twin BELONGS TO, for the Opening hours step's *Copy another
 * twin* (spec twin-details-page-hours-start, R4.1): the twin's own `organisation_id` matched by `id`
 * against the organisation list (`matchTwinOrganisation`, the rule `useTwinOrganisationName` uses —
 * never the operator's selected organisation), then that organisation's twins, less this one, by
 * name.
 *
 * `pending` while it reads; `failed` when either read fails, the twin has no organisation, or the
 * list holds no organisation with its id — the copy card then says nothing about how many twins
 * there are, since "we could not find out" is not "there are none"; `ready` with the twins
 * otherwise, which may be none. To anyone but a TL_ADMIN, the list's middleware route returns only
 * twins in the customer-visible lifecycle stages — waitingForCCTV, liveTwin, plannedPause, suspended
 * and dataFlowing (`organisations.service.ts` `getTwins`).
 *
 * ## Bounds (`docs/coding-standards.md`, Outbound calls and list reads)
 *
 * One 30 s deadline (`MIDDLEWARE_REQUEST_TIMEOUT_MS`) across the token and both requests; each
 * request's `signal` is the authoritative bound and axios's `timeout` the secondary guard. No retry:
 * the copy popover's *Try again* is the person's. Both lists are unpaginated middleware reads — 14
 * organisations and at most 11 twins in one on staging, far below the platform's 1,000-row cap. An
 * ANSWER is cached per organisation for the life of the page; a FAILURE is not, and is read again on
 * the next arrival or *Try again*. A changed twin, a departure or an unmount aborts the read and
 * drops its answer.
 *
 * When the page has no organisation id for the twin (`null`: none, or the twin's record could not
 * be read), *Try again* reads the twin's record again here first — under its own 30 s deadline —
 * and goes on with the organisation that record names. The page's own read is not re-run: the
 * landing and the Twin details seed hang on it.
 */

export interface OrganisationTwin {
  bid: string;
  name: string;
}

export type OrganisationTwins =
  | { status: 'pending' }
  | { status: 'failed' }
  | { status: 'ready'; orgName: string; twins: OrganisationTwin[] };

export interface OrganisationTwinsParams {
  /**
   * The twin's own `organisation_id`: a number; `null` when the twin is known and has none;
   * `undefined` while the twin itself is not known yet.
   */
  organisationId: number | null | undefined;
  /** This twin, left out of the list. */
  twinBid: string | null | undefined;
  /** Read only while the start panel is on screen. */
  active: boolean;
  /** The middleware base URL. */
  baseUrl: string;
  /** The raw Auth0 token getter; bounded here. */
  getToken: () => Promise<string>;
}

const byName = (a: OrganisationTwin, b: OrganisationTwin) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

export const useOrganisationTwins = ({
  organisationId,
  twinBid,
  active,
  baseUrl,
  getToken,
}: OrganisationTwinsParams): { state: OrganisationTwins; retry: () => void } => {
  /** Organisations already read: their name and every twin, by name. */
  const cache = useRef(new Map<number, { orgName: string; twins: OrganisationTwin[] }>());
  const [, setVersion] = useState(0);
  /** The organisation whose last read failed, until the next read of it starts. */
  const [failedFor, setFailedFor] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);
  /**
   * The twin's record as *Try again* read it again, for a twin the page has no organisation id for:
   * `undefined` while that read runs, `null` when it found none. Keyed by twin.
   */
  const [reread, setReread] = useState<{
    twinBid: string | null | undefined;
    organisationId: number | null | undefined;
  } | null>(null);
  const resolvedId =
    organisationId === null && reread !== null && reread.twinBid === twinBid
      ? reread.organisationId
      : organisationId;
  const organisationIdRef = useRef(organisationId);
  organisationIdRef.current = organisationId;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  /** Twins already reported as having no organisation found, so the warning is said once. */
  const warned = useRef(new Set<string>());
  const twinBidRef = useRef(twinBid);
  twinBidRef.current = twinBid;

  /** `organisationId` null: the twin has none, or its record could not be read; a number: not in the list. */
  const warnNotFound = useCallback((organisationId: number | null) => {
    const key = twinBidRef.current ?? '';
    if (warned.current.has(key)) return;
    warned.current.add(key);
    // eslint-disable-next-line no-console
    console.warn(
      organisationId === null
        ? '[hours-start] twin has no organisation_id (or its record could not be read):'
        : `[hours-start] organisation ${organisationId} is not in the organisation list for twin`,
      twinBidRef.current,
    );
  }, []);

  useEffect(() => {
    if (active && organisationId === null) warnNotFound(null);
  }, [active, organisationId, warnNotFound]);

  useEffect(() => {
    if (!active || organisationId !== null || reread === null) return;
    if (reread.twinBid !== twinBid || reread.organisationId !== undefined) return;
    const bid = twinBid;
    if (!bid) {
      setReread({ twinBid, organisationId: null });
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const timer = setTimeout(() => controller.abort(), MIDDLEWARE_REQUEST_TIMEOUT_MS);
    void (async () => {
      let found: number | null = null;
      try {
        const token = await getTokenBounded(
          () => getTokenRef.current(),
          MIDDLEWARE_REQUEST_TIMEOUT_MS,
        );
        // `getTwinById` swallows its own errors (and logs them): null is a failure or no such twin.
        const twin = await getTwinById(baseUrl, token, bid, {
          timeout: MIDDLEWARE_REQUEST_TIMEOUT_MS,
          signal: controller.signal,
        });
        found = twin?.organisation_id ?? null;
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(
          '[hours-start] organisation twins could not be read:',
          'twin',
          err instanceof Error ? err.message : String(err),
        );
      }
      if (cancelled) return;
      setReread({ twinBid: bid, organisationId: found });
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, organisationId, reread, twinBid, baseUrl]);

  useEffect(() => {
    if (!active || resolvedId == null || cache.current.has(resolvedId)) return;
    const id = resolvedId;
    setFailedFor(f => (f === id ? null : f));
    const controller = new AbortController();
    /** Set by the cleanup — a changed twin, a departure or an unmount — never by the deadline. */
    let cancelled = false;
    const deadline = Date.now() + MIDDLEWARE_REQUEST_TIMEOUT_MS;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
    const remaining = () => {
      const left = deadline - Date.now();
      if (left <= 0) throw new Error(TIMED_OUT_MESSAGE);
      return left;
    };
    void (async () => {
      /** Which read failed, for the log: the hook is the one place that can name it. */
      let step: 'token' | 'organisations' | 'twins' = 'token';
      try {
        const token = await getTokenBounded(() => getTokenRef.current(), remaining());
        step = 'organisations';
        const organisations = await getOrganisations(baseUrl, token, {
          timeout: remaining(),
          signal: controller.signal,
        });
        if (cancelled) return;
        // `getOrganisations` swallows its own errors (and logs the status): undefined is a failure.
        if (!organisations) throw new Error('the organisation list could not be read');
        const organisation = matchTwinOrganisation(organisations, id);
        if (!organisation) {
          warnNotFound(id);
          setFailedFor(id);
          return;
        }
        step = 'twins';
        const twins = await getOrgTwins(baseUrl, token, organisation.bid, {
          timeout: remaining(),
          signal: controller.signal,
        });
        if (cancelled) return;
        // `null` is a failed read; `[]` an organisation with no twins (`getOrgTwins`' contract).
        if (!twins) throw new Error('the organisation’s twins could not be read');
        cache.current.set(id, {
          orgName: organisation.name,
          twins: twins.map(t => ({ bid: t.bid, name: t.name || t.bid })).sort(byName),
        });
        setVersion(v => v + 1);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error(
          '[hours-start] organisation twins could not be read:',
          step,
          timedOut ? TIMED_OUT_MESSAGE : err instanceof Error ? err.message : String(err),
        );
        // Not cached: the next arrival, or *Try again*, reads again.
        setFailedFor(id);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // `getToken` is read through its ref; `attempt` is *Try again*.
  }, [active, resolvedId, baseUrl, attempt, warnNotFound]);

  const retry = useCallback(() => {
    // No organisation id from the page: the twin's record is read again first.
    if (organisationIdRef.current === null) {
      setReread({ twinBid: twinBidRef.current, organisationId: undefined });
    }
    setAttempt(a => a + 1);
  }, []);

  let state: OrganisationTwins;
  if (resolvedId === undefined) state = { status: 'pending' };
  else if (resolvedId === null) state = { status: 'failed' };
  else {
    const hit = cache.current.get(resolvedId);
    state = hit
      ? { status: 'ready', orgName: hit.orgName, twins: hit.twins.filter(t => t.bid !== twinBid) }
      : failedFor === resolvedId
        ? { status: 'failed' }
        : { status: 'pending' };
  }
  return { state, retry };
};
